/**
 * Where our surfaces are served (spec I). `hosts.json` is generated from the
 * Vercel domains of the five tracked projects by `scripts/posthog/hosts.ts`;
 * never import V3's `partners.ts` outside crm-v3 (it reads `import.meta.env`).
 *
 * This file ships in every client bundle (crm-app, crm-v3, studio-*), so it
 * lists Tagada-owned hosts only (`isTagadaOwnedHost`). The white-label partner
 * hosts serving CRM V2 live server-side in `scripts/posthog/partner-hosts.json`
 * (PostHog guard and insights): a partner host must never see another
 * partner's domain in the JS it downloads. In a client, any host that is not
 * listed here and runs the CRM V2 bundle is a partner host of `crm_v2`.
 */

import hostsFile from './hosts.json';

export type HostProject = 'landing' | 'crm_v3' | 'crm_v2' | 'studio_v3' | 'studio_v2';

export const HOSTS: { readonly [K in HostProject]: string[] } = {
  landing: [...hostsFile.projects.landing],
  crm_v3: [...hostsFile.projects.crm_v3],
  crm_v2: [...hostsFile.projects.crm_v2],
  studio_v3: [...hostsFile.projects.studio_v3],
  studio_v2: [...hostsFile.projects.studio_v2],
};

/** Domains we own: `hosts.json` lists only hosts under these (Vercel previews included). */
export const TAGADA_OWNED_DOMAINS = [
  'tagada.io',
  'tagada.dev',
  'tagadapay.com',
  'tagadapay.dev',
  'tagadapay.io',
  'vercel.app',
] as const;

/** True for a host under a Tagada-owned domain; false for partner (white-label) hosts. */
export function isTagadaOwnedHost(hostname: string): boolean {
  const host = bareHost(hostname);
  return TAGADA_OWNED_DOMAINS.some((d) => isUnder(host, d));
}

export const HOSTS_GENERATED_AT: string = hostsFile.generatedAt;

export type Environment = 'production' | 'staging' | 'local';

function bareHost(hostname: string): string {
  const lower = hostname.trim().toLowerCase();
  // `location.hostname` has no port, but a `host` value does: tolerate both.
  return lower.startsWith('[') ? lower : lower.replace(/:\d+$/, '');
}

function isUnder(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/**
 * `environment` of a client event (A4): `localhost`/`127.0.0.1` (and the
 * reserved `*.localhost` names of the local DevLauncher) are `local`;
 * `*.tagada.dev` and `*.vercel.app` are `staging`; anything else, partner
 * hosts included, is `production`.
 */
export function environmentFromHost(hostname: string): Environment {
  const host = bareHost(hostname);
  if (host === '127.0.0.1' || isUnder(host, 'localhost')) return 'local';
  if (isUnder(host, 'tagada.dev') || isUnder(host, 'vercel.app')) return 'staging';
  return 'production';
}

/** Server `environment` from `VERCEL_ENV`: never the raw `preview`. */
export function environmentFromVercelEnv(vercelEnv: string | undefined): Environment {
  if (vercelEnv === 'production') return 'production';
  if (vercelEnv === 'preview') return 'staging';
  return 'local';
}
